import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Carousel, Dropdown } from 'react-bootstrap';

import { UseOutletContextProduct } from './Product';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProductDA } from '../../../../services/admin/product.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Message } from '../../../../models/message.model';
import { CompanyProducts } from '../../../../models/company.products.model';
import { Product } from '../../../../models/product.model';
import { imageProduct } from '../../../../scripts/image.value.script';
import { datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { getError, getMessage } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';
import AppInventoryListHeader from '../component/InventoryListHeader';
import imgProductDefault from '../../../../assets/themes/product.png';

export interface ProductListPageProps {};

let errorResponse: Error, messageResponse: Message, productsResponse: CompanyProducts;

const ProductListPage: React.FunctionComponent<ProductListPageProps> = props => {
  const {setRoute, companyForUser, loadProductsForCompany} = UseOutletContextProduct()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({search: '', options: {category: '', sort_field: '', sort_mode: ''}})
  const [checked, setChecked] = useState({count: 0, list: [] as string[]})
  const [pagination, setPagination] = useState({current: 1, total: 0, pages: 1, limit: 20})
  const [products, setProducts] = useState<Product[] | null>(null)

  const loadProducts = async (id_company: string, callback: any, page: number = 1, limit: number = pagination.limit) => {
    await ProductDA.getProducts(id_company, filter.search, filter.options.category, filter.options.sort_field, filter.options.sort_mode, page, limit).then( (response) => {
      if (response.status === 200) {
        productsResponse = response.data?.data ?? response.data
        const productItems = productsResponse.items ?? productsResponse.products ?? []
        const pg = productsResponse.pagination ?? { page, limit: pagination.limit, total: productItems.length, pages: 1 }

        setProducts(productItems)
        setChecked({count: 0, list: []})
        setPagination({current: pg.page ?? page, total: pg.total ?? productItems.length, pages: pg.pages ?? 1, limit: pg.limit ?? pagination.limit})
        callback()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleChangePage = (page: number, newLimit?: number) => {
    if (companyForUser) loadProducts(companyForUser.id, () => {}, page, newLimit ?? pagination.limit)
  }

  const submitProductDelete = (item: string) => {
    if (companyForUser) {
      GlobalSwal.fire({
        title: lang.labels.youWantDeleteProduct,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ProductDA.deleteProducts(companyForUser.id, [item]).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data

              loadProducts(companyForUser.id, () => {
                loadProductsForCompany(companyForUser.id)

                GlobalSwal.fire({
                  title: getMessage(messageResponse.message, lang.code),
                  text: lang.labels.actionCompletedReturningToPage,
                  icon: 'success',
                  showConfirmButton: false,
                  timer: 2200
                })
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitProductUpdate = (item: string) => {
    navigate("/main/inventory/product/update/" + item)
  }

  const handleChangeSelectAllProducts = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let list: string[] = (event.target.checked) ? event.target.value.split(',') : []
    setChecked({count: list.length, list: list})
  }

  const handleChangeSelectProduct = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let list: string[] = (event.target.checked) ? (!checked.list.includes(event.target.value)) ? checked.list.concat(event.target.value) : checked.list : checked.list.filter((item) => (item !== event.target.value))
    setChecked({count: list.length, list: list})
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      sortColumn(item, filter.options)
      setFilter({search: filter.search, options: {...filter.options}})
      loadProducts(companyForUser.id, () => {})
    }
  }

  function productVariety(quantity: number): number {
    switch(Math.floor(quantity / 10)) {
      case 0:
        return 1
      case 1:
        return 2
      case 2:
        return 3
      case 3:
        return 4
      default:
        return 5
    }
  }

  useEffect( () => {
    if (companyForUser) {
      filter.search = ''
      filter.options.category = ''
      filter.options.sort_field = ''
      filter.options.sort_mode = ''
      setFilter({search: '', options: {category: '', sort_field: '', sort_mode: ''}})

      setProducts(null)
      loadProducts(companyForUser.id, () => {})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.products, branch: lang.labels.listProducts}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { products
        ?
        <div className="card card-flush">
          <AppInventoryListHeader inventory={"product"} filter={filter} setFilter={setFilter} checked={checked} loadInventory={loadProducts}></AppInventoryListHeader>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-25px">
                        <div className="form-check form-check-sm form-check-custom form-check-solid">
                          <input className="form-check-input" type="checkbox" value={products.map(item => item.id)} checked={products.length === checked.count && checked.count > 0} onChange={handleChangeSelectAllProducts} />
                        </div>
                      </th>
                      <th className="min-w-250px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("product")}>
                          <span className="me-1">{lang.labels.product}</span>
                          <AppSortTable attribute={"product"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("category")}>
                          <span className="me-1">{lang.labels.category}</span>
                          <AppSortTable attribute={"category"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("brand")}>
                          <span className="me-1">{lang.labels.brand}</span>
                          <AppSortTable attribute={"brand"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("tags")}>
                          <span className="me-1">{lang.labels.tags}</span>
                          <AppSortTable attribute={"tags"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("last modified")}>
                          <span className="me-1">{lang.labels.lastModified}</span>
                          <AppSortTable attribute={"last modified"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("variants")}>
                          <span className="me-1">{lang.labels.variants}</span>
                          <AppSortTable attribute={"variants"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { products.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { products.map (( (item, index) => { return (
                        <tr key={index} className={`row-dashed ${(index + 1) % 2 !== 0 ? 'odd' : 'even'}`}>
                          <td>
                            <div className="form-check form-check-sm form-check-custom form-check-solid">
                              <input className="form-check-input" type="checkbox" value={item.id} checked={checked.list.includes(item.id)} onChange={handleChangeSelectProduct} />
                            </div>
                          </td>
                          <td className="d-flex align-items-center">
                            <div className="me-5">
                              <Link to={"/main/inventory/product/view/" + item.id}>
                                { item.photos.length > 0
                                  ?
                                  <Carousel slide={false} controls={false} indicators={false} touch={false}>
                                    { item.photos.map (( (item, index) => { return (
                                      <Carousel.Item key={index}>
                                        <img src={imageProduct(item)} className="d-block w-50px h-50px" alt={`${lang.labels.photography}${suffixLegend(index)}`} />
                                      </Carousel.Item>
                                    )}))}
                                  </Carousel>
                                  :
                                  <div className="symbol symbol-50px overflow-hidden">
                                    <span className="symbol-label">
                                      <img src={imgProductDefault} alt={lang.labels.photography} className="w-100 h-100" />
                                    </span>
                                  </div>
                                }
                              </Link>
                            </div>
                            <Link to={"/main/inventory/product/view/" + item.id} className="fw-bolder text-dark text-hover-primary">{item.display_name}</Link>
                          </td>
                          <td>{item.category?.display_name ?? ''}</td>
                          <td>{typeof item.brand === 'object' ? (item.brand as any)?.name ?? '' : (item.brand ?? '')}</td>
                          <td>
                            { (item.tags?.length ?? 0) > 0
                              ?
                              <>
                                { item.tags.map (( (sub_item, sub_index) => { return (
                                  <div key={sub_index} className="badge badge-light m-1">{sub_item}</div>
                                )}))}
                              </>
                              :
                              <small>{lang.labels.none}</small>
                            }
                          </td>
                          <td className="text-nowrap fs-7">{datetimeFormat12h(lang.code, item.updated_at)}</td>
                          <td className="text-nowrap text-end">
                            <div className="rating justify-content-end align-content-center">
                              { Array.from ( {length: productVariety(item.variants.length)}, (_, index) => (
                                <div key={index} className={`rating-label ${item.variants.length > 0 ? "checked" : "unchecked"}`}>
                                  <span className="svg-icon svg-icon-3">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M11.1359 4.48359C11.5216 3.82132 12.4784 3.82132 12.8641 4.48359L15.011 8.16962C15.1523 8.41222 15.3891 8.58425 15.6635 8.64367L19.8326 9.54646C20.5816 9.70867 20.8773 10.6186 20.3666 11.1901L17.5244 14.371C17.3374 14.5803 17.2469 14.8587 17.2752 15.138L17.7049 19.382C17.7821 20.1445 17.0081 20.7069 16.3067 20.3978L12.4032 18.6777C12.1463 18.5645 11.8537 18.5645 11.5968 18.6777L7.69326 20.3978C6.99192 20.7069 6.21789 20.1445 6.2951 19.382L6.7248 15.138C6.75308 14.8587 6.66264 14.5803 6.47558 14.371L3.63339 11.1901C3.12273 10.6186 3.41838 9.70867 4.16744 9.54646L8.3365 8.64367C8.61089 8.58425 8.84767 8.41222 8.98897 8.16962L11.1359 4.48359Z" fill="currentColor"></path>
                                    </svg>
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="text-end">
                            <Dropdown>
                              <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
                                <div className="d-flex align-items-center">
                                  {lang.labels.actions}
                                  <span className="svg-icon svg-icon-5 me-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                                    </svg>
                                  </span>
                                </div>
                              </Dropdown.Toggle>
                              <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }} variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                                <Dropdown.Item bsPrefix="menu-item" onClick={() => {submitProductUpdate(item.id)} }>
                                  <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.edit}</p>
                                </Dropdown.Item>
                                <Dropdown.Item bsPrefix="menu-item" onClick={() => submitProductDelete(item.id)}>
                                  <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.delete}</p>
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={8} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination
                showOption={true}
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  limit: pagination.limit,
                  pages: Array.from({ length: pagination.pages }, (_, i) => i + 1),
                }}
                loadPagination={handleChangePage}
              ></AppPagination>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default ProductListPage;
